import type { CollaborationFixture } from "../domain/types";

export function deepFreeze<T>(value: T): Readonly<T> {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const nested of Object.values(value)) deepFreeze(nested);
  }
  return value;
}

const sourceFixture: CollaborationFixture = {
  fixtureVersion: "c0.1",
  generatedAt: "2026-08-09T08:30:00Z",
  activeProjectId: "prj-northbank",
  projects: [
    {
      id: "prj-northbank",
      code: "NB-041",
      name: "Northbank Civic Quarter",
      stage: "Technical design",
      role: "Design coordination",
    },
    {
      id: "prj-harbour",
      code: "HC-018",
      name: "Harbour Campus",
      stage: "Concept design",
      role: "Project member",
    },
  ],
  federation: {
    id: "fed-nb-2026-08-09",
    name: "Coordination federation 32",
    revisionLabel: "F32",
    completeness: "partial",
    references: [
      {
        id: "ref-arc",
        externalIdentifier: "mdl:arc:rev-228",
        name: "Architecture",
        discipline: "ARC",
        revision: "P08",
        source: "Design authoring team",
        referenceState: "resolved",
        receivedAt: "2026-08-09T07:42:00Z",
        unknownFields: { deliveryTag: "S4", sourceColour: "#d6c7ae" },
      },
      {
        id: "ref-str",
        externalIdentifier: "mdl:str:rev-191",
        name: "Structure",
        discipline: "STR",
        revision: "P06",
        source: "Structural consultant",
        referenceState: "stale",
        receivedAt: "2026-08-06T12:10:00Z",
        unknownFields: { supersededBy: "mdl:str:rev-194" },
      },
      {
        id: "ref-mep",
        externalIdentifier: "mdl:mep:rev-077",
        name: "Building services",
        discipline: "MEP",
        revision: "P04",
        source: "Services consultant",
        referenceState: "missing",
        receivedAt: "2026-08-08T16:20:00Z",
        unknownFields: { retryAfter: "2026-08-10T08:00:00Z" },
      },
      {
        id: "ref-land",
        externalIdentifier: "mdl:land:rev-014",
        name: "Landscape",
        discipline: "LAN",
        revision: "P02",
        source: "Landscape architect",
        referenceState: "unsupported",
        receivedAt: "2026-08-07T09:15:00Z",
        unknownFields: { format: "application/vnd.landscape-scene+json", schema: "vNext" },
      },
      {
        id: "ref-sec",
        externalIdentifier: "mdl:security:rev-009",
        name: "Security zones",
        discipline: "SEC",
        revision: "P01",
        source: "Security consultant",
        referenceState: "refused",
        receivedAt: "2026-08-08T10:05:00Z",
        unknownFields: { refusalCode: "subject_scope_mismatch" },
      },
    ],
    elements: [
      {
        id: "element-door-b-17",
        modelReferenceId: "ref-arc",
        modelRevisionIdentifier: "mdl:arc:rev-228",
        elementIdentifier: "ifc-guid:2Z9m7KNWz8y9R0dB17Door",
        label: "FD-B-17 · Fire door at stair core B",
        elementType: "IfcDoor",
        referenceState: "resolved",
        locator: { scheme: "fixture.element-locator.v1", expressId: 1842, fragment: "core-b/level-03" },
        unknownFields: { authoringElementClass: "Door.FireRated" },
      },
      {
        id: "element-beam-tb-04",
        modelReferenceId: "ref-str",
        modelRevisionIdentifier: "mdl:str:rev-191",
        elementIdentifier: "ifc-guid:1B4m8TransferBeam04",
        label: "TB-04 · Transfer beam",
        elementType: "IfcBeam",
        referenceState: "stale",
        locator: { scheme: "fixture.element-locator.v1", expressId: 941, fragment: "zone-3/level-02" },
        unknownFields: { supersededBy: "mdl:str:rev-194" },
      },
      {
        id: "element-land-view-01",
        modelReferenceId: "ref-land",
        modelRevisionIdentifier: "mdl:land:rev-014",
        elementIdentifier: "vendor:landscape:bio-zone-01",
        label: "BZ-01 · Biodiversity zone",
        elementType: "VendorLandscapeZone",
        referenceState: "unsupported",
        locator: { scheme: "vendor.landscape-locator.v2", zone: "bio-01" },
        unknownFields: { sourceCoordinateSystem: "local-grid-vNext" },
      },
    ],
    // Multiple positiveRevision options for the same model (ref-arc has
    // both a superseded P07 and the current P08), demonstrating that a
    // federation can hold more than one revision of a model at once while
    // still exposing exactly one active revision per model.
    revisions: [
      {
        id: "rev-arc-227",
        modelReferenceId: "ref-arc",
        projectId: "prj-northbank",
        modelId: "model-arc",
        positiveRevision: 227,
        revisionLabel: "P07",
        externalIdentifier: "mdl:arc:rev-227",
        isActive: false,
        referenceState: "stale",
        receivedAt: "2026-08-05T09:00:00Z",
        unknownFields: { supersededBy: "mdl:arc:rev-228" },
      },
      {
        id: "rev-arc-228",
        modelReferenceId: "ref-arc",
        projectId: "prj-northbank",
        modelId: "model-arc",
        positiveRevision: 228,
        revisionLabel: "P08",
        externalIdentifier: "mdl:arc:rev-228",
        isActive: true,
        referenceState: "resolved",
        receivedAt: "2026-08-09T07:42:00Z",
        unknownFields: {},
      },
      {
        id: "rev-str-191",
        modelReferenceId: "ref-str",
        projectId: "prj-northbank",
        modelId: "model-str",
        positiveRevision: 191,
        revisionLabel: "P06",
        externalIdentifier: "mdl:str:rev-191",
        isActive: true,
        referenceState: "stale",
        receivedAt: "2026-08-06T12:10:00Z",
        unknownFields: { supersededBy: "mdl:str:rev-194" },
      },
    ],
    // CDE UI-local visibility toggles (see src/domain/types.ts's
    // ElementVisibilityState comment: not a BIM-Hub authority signal).
    visibilityStates: [
      {
        id: "visibility-door-b-17",
        elementReferenceId: "element-door-b-17",
        visibility: "visible",
        setBy: "subject:fixture:coordinator",
        setAt: "2026-08-09T08:00:00Z",
        unknownFields: {},
      },
      {
        id: "visibility-beam-tb-04",
        elementReferenceId: "element-beam-tb-04",
        visibility: "isolated",
        setBy: "subject:fixture:mep-lead",
        setAt: "2026-08-09T08:05:00Z",
        unknownFields: { reason: "clash-review" },
      },
      {
        id: "visibility-land-view-01",
        elementReferenceId: "element-land-view-01",
        visibility: "hidden",
        setBy: "subject:fixture:coordinator",
        setAt: "2026-08-08T10:00:00Z",
        unknownFields: {},
      },
    ],
    // Standalone federation-level saved viewpoints (distinct from an
    // individual issue's viewpoint), spanning several referenceStates to
    // exercise the same 5-state accounting used elsewhere in this fixture.
    viewpoints: [
      {
        id: "vp-zone-3-clash-01",
        label: "Zone 3 clash review",
        createdBy: "subject:fixture:mep-lead",
        createdAt: "2026-08-09T08:10:00Z",
        referenceState: "resolved",
        locator: { scheme: "fixture.viewpoint.v1", camera: "zone-3-clash-01", target: "ifc-guid:3DuctAHU04" },
        unknownFields: { clippingPlane: "ceiling-service-zone" },
      },
      {
        id: "vp-core-b-door-clearance",
        label: "Core B door clearance",
        createdBy: "subject:fixture:coordinator",
        createdAt: "2026-08-09T08:12:00Z",
        referenceState: "stale",
        locator: { scheme: "fixture.viewpoint.v1", camera: "core-b-door-clearance", target: "ifc-guid:2Z9m7KNWz8y9R0dB17Door" },
        unknownFields: { sourceRevision: "P06" },
      },
      {
        id: "vp-landscape-walkthrough",
        label: "Landscape biodiversity walkthrough",
        createdBy: "subject:fixture:landscape",
        createdAt: "2026-08-07T12:05:00Z",
        referenceState: "unsupported",
        locator: { scheme: "vendor.viewpoint.v2", path: "landscape/bio-01" },
        unknownFields: { cameraMode: "walkthrough" },
      },
    ],
  },
  documents: [
    {
      id: "doc-001",
      documentNumber: "NB-ARC-SPC-4101",
      title: "External envelope performance specification",
      revision: "C03",
      kind: "Specification",
      owner: "Field & Mason",
      modifiedAt: "2026-08-09T08:02:00Z",
      referenceState: "resolved",
      indexCompleteness: "complete",
      unknownFields: { suitabilityCode: "A2" },
    },
    {
      id: "doc-002",
      documentNumber: "NB-MEP-SCH-2208",
      title: "Plant equipment schedule",
      revision: "P04",
      kind: "Schedule",
      owner: "Atelier MEP",
      modifiedAt: "2026-08-08T15:25:00Z",
      referenceState: "stale",
      indexCompleteness: "partial",
      unknownFields: { reviewPurpose: "coordination" },
    },
    {
      id: "doc-003",
      documentNumber: "NB-CIV-DRG-0024",
      title: "External levels and drainage",
      revision: "P01",
      kind: "Drawing",
      owner: "North Civil",
      modifiedAt: "2026-08-08T09:30:00Z",
      referenceState: "missing",
      indexCompleteness: "partial",
      unknownFields: { transmittal: "TR-1084", originSystem: "LegacyDocs" },
    },
    {
      id: "doc-004",
      documentNumber: "NB-PMO-MIN-0132",
      title: "Design coordination meeting 32",
      revision: "C01",
      kind: "Minutes",
      owner: "Project office",
      modifiedAt: "2026-08-07T17:45:00Z",
      referenceState: "resolved",
      indexCompleteness: "complete",
      unknownFields: {},
    },
    {
      id: "doc-005",
      documentNumber: "NB-LAN-REP-0018",
      title: "Landscape biodiversity schedule",
      revision: "P02",
      kind: "Report",
      owner: "Green Works",
      modifiedAt: "2026-08-07T12:05:00Z",
      referenceState: "unsupported",
      indexCompleteness: "partial",
      unknownFields: { indexFormat: "application/vnd.landscape-index+json" },
    },
    {
      id: "doc-006",
      documentNumber: "NB-SEC-DRG-0011",
      title: "Security zoning diagram",
      revision: "P01",
      kind: "Drawing",
      owner: "Secure Design",
      modifiedAt: "2026-08-06T14:10:00Z",
      referenceState: "refused",
      indexCompleteness: "partial",
      unknownFields: { refusalCode: "subject_scope_mismatch" },
    },
  ],
  documentIndexCompleteness: "partial",
  issues: [
    {
      id: "issue-041",
      externalIdentifier: "BCF-NB-041",
      title: "Supply duct conflicts with transfer beam",
      topicType: "Clash",
      priority: "critical",
      assignee: "Atelier MEP",
      dueDate: "2026-08-11",
      workflowState: "open",
      referenceState: "resolved",
      syncCompleteness: "complete",
      elementReferences: [
        {
          id: "issue-041-duct",
          modelReferenceId: "ref-mep",
          modelRevisionIdentifier: "mdl:mep:rev-077",
          elementIdentifier: "ifc-guid:3DuctAHU04",
          label: "AHU-04 supply duct",
          elementType: "IfcDuctSegment",
          referenceState: "missing",
          locator: { scheme: "fixture.element-locator.v1", expressId: 2411, fragment: "zone-3/ceiling" },
          unknownFields: { systemCode: "SA-04" },
        },
        {
          id: "issue-041-beam",
          modelReferenceId: "ref-str",
          modelRevisionIdentifier: "mdl:str:rev-191",
          elementIdentifier: "ifc-guid:1B4m8TransferBeam04",
          label: "TB-04 · Transfer beam",
          elementType: "IfcBeam",
          referenceState: "stale",
          locator: { scheme: "fixture.element-locator.v1", expressId: 941, fragment: "zone-3/level-02" },
          unknownFields: { supersededBy: "mdl:str:rev-194" },
        },
      ],
      viewpoint: {
        referenceState: "missing",
        locator: { scheme: "fixture.viewpoint.v1", camera: "zone-3-clash-01", target: "ifc-guid:3DuctAHU04" },
        unknownFields: { clippingPlane: "ceiling-service-zone" },
      },
      unknownFields: { labels: ["zone-3", "coordination"] },
    },
    {
      id: "issue-037",
      externalIdentifier: "BCF-NB-037",
      title: "Fire door clearance at stair core B",
      topicType: "Design",
      priority: "high",
      assignee: "Field & Mason",
      dueDate: "2026-08-12",
      workflowState: "in-review",
      referenceState: "stale",
      syncCompleteness: "partial",
      elementReferences: [
        {
          id: "issue-037-door",
          modelReferenceId: "ref-arc",
          modelRevisionIdentifier: "mdl:arc:rev-221",
          elementIdentifier: "ifc-guid:2Z9m7KNWz8y9R0dB17Door",
          label: "FD-B-17 · Fire door at stair core B",
          elementType: "IfcDoor",
          referenceState: "stale",
          locator: { scheme: "fixture.element-locator.v1", expressId: 1788, fragment: "core-b/level-03" },
          unknownFields: { sourceRevision: "P06" },
        },
      ],
      viewpoint: {
        referenceState: "stale",
        locator: { scheme: "fixture.viewpoint.v1", camera: "core-b-door-clearance", target: "ifc-guid:2Z9m7KNWz8y9R0dB17Door" },
        unknownFields: { sourceRevision: "P06" },
      },
      unknownFields: { extensionTopicStatus: "technical-review" },
    },
    {
      id: "issue-028",
      externalIdentifier: "BCF-NB-028",
      title: "Confirm maintenance zone above AHU-04",
      topicType: "Clearance",
      priority: "normal",
      assignee: "Atelier MEP",
      dueDate: "2026-08-14",
      workflowState: "open",
      referenceState: "missing",
      syncCompleteness: "complete",
      elementReferences: [
        {
          id: "issue-028-ah04",
          modelReferenceId: "ref-mep",
          modelRevisionIdentifier: "mdl:mep:rev-077",
          elementIdentifier: "ifc-guid:0AHU04",
          label: "AHU-04",
          elementType: "IfcUnitaryEquipment",
          referenceState: "missing",
          locator: { scheme: "fixture.element-locator.v1", expressId: 2298, fragment: "plant/level-04" },
          unknownFields: { originalTopicReference: "maint-zone-above" },
        },
      ],
      unknownFields: {},
    },
    {
      id: "issue-019",
      externalIdentifier: "BCF-NB-019",
      title: "Grid reference corrected on podium slab",
      topicType: "Documentation",
      priority: "low",
      assignee: "North Structures",
      dueDate: "2026-08-06",
      workflowState: "closed",
      referenceState: "refused",
      syncCompleteness: "complete",
      elementReferences: [
        {
          id: "issue-019-grid",
          modelReferenceId: "ref-sec",
          modelRevisionIdentifier: "mdl:security:rev-009",
          elementIdentifier: "vendor:security:grid-podium-a",
          label: "Podium grid reference",
          elementType: "VendorSecurityZone",
          referenceState: "refused",
          locator: { scheme: "vendor.security-locator.v1", zone: "podium-a" },
          unknownFields: { refusalCode: "subject_scope_mismatch" },
        },
      ],
      unknownFields: { importedStatus: "done" },
    },
    {
      id: "issue-011",
      externalIdentifier: "BCF-NB-011",
      title: "Landscape viewpoint extension cannot be rendered",
      topicType: "Viewpoint",
      priority: "normal",
      assignee: "Green Works",
      dueDate: "2026-08-18",
      workflowState: "open",
      referenceState: "unsupported",
      syncCompleteness: "partial",
      elementReferences: [
        {
          id: "issue-011-land",
          modelReferenceId: "ref-land",
          modelRevisionIdentifier: "mdl:land:rev-014",
          elementIdentifier: "vendor:landscape:bio-zone-01",
          label: "BZ-01 · Biodiversity zone",
          elementType: "VendorLandscapeZone",
          referenceState: "unsupported",
          locator: { scheme: "vendor.landscape-locator.v2", zone: "bio-01" },
          unknownFields: { sourceCoordinateSystem: "local-grid-vNext" },
        },
      ],
      viewpoint: {
        referenceState: "unsupported",
        locator: { scheme: "vendor.viewpoint.v2", path: "landscape/bio-01" },
        unknownFields: { cameraMode: "walkthrough" },
      },
      unknownFields: { viewpointFormat: "vendor.camera-path.v2" },
    },
  ],
  issueSyncCompleteness: "partial",
  scheduleBoundaryScenarios: [
    {
      id: "schedule-boundary-unlinked-rfi",
      cdeRecordIdentifier: "RFI-0131",
      cdeRecordKind: "RFI",
      cdeDueDate: "2026-08-15",
      relationship: "unlinked",
      annotation: "CDE workflow due date only; no Controls task relationship was supplied.",
    },
    {
      id: "schedule-boundary-explicit-issue",
      cdeRecordIdentifier: "BCF-NB-041",
      cdeRecordKind: "Issue",
      cdeDueDate: "2026-08-11",
      relationship: "explicit-candidate",
      candidateControlsReference: {
        scheme: "fixture.controls-link-candidate.v1",
        opaqueIdentifier: "controls:task:NB-WP-023",
        source: "explicit-fixture-declaration",
      },
      annotation: "Candidate supplied explicitly by the fixture; it is not inferred from title or date and is not a consumer contract.",
    },
  ],
  rfis: [
    {
      id: "RFI-0128",
      subject: "Roof build-up at plant enclosure",
      raisedBy: "Field & Mason",
      owner: "Client design team",
      dueDate: "2026-08-10",
      status: "overdue",
      linkedRecords: 4,
    },
    {
      id: "RFI-0131",
      subject: "Loading requirement for public art plinth",
      raisedBy: "North Structures",
      owner: "Project office",
      dueDate: "2026-08-15",
      status: "open",
      linkedRecords: 2,
    },
    {
      id: "RFI-0122",
      subject: "Finish reference for atrium soffit",
      raisedBy: "Main contractor",
      owner: "Field & Mason",
      dueDate: "2026-08-05",
      status: "answered",
      linkedRecords: 3,
    },
  ],
  approvals: [
    {
      id: "APR-0084",
      subject: "Stage 4 coordinated design package",
      package: "PKG-NB-044",
      submittedBy: "Project office",
      submittedAt: "2026-08-09T06:50:00Z",
      status: "awaiting-review",
      reviewersComplete: 2,
      reviewersTotal: 5,
    },
    {
      id: "APR-0079",
      subject: "Facade visual mock-up records",
      package: "PKG-NB-039",
      submittedBy: "Field & Mason",
      submittedAt: "2026-08-07T10:15:00Z",
      status: "changes-requested",
      reviewersComplete: 4,
      reviewersTotal: 4,
    },
    {
      id: "APR-0072",
      subject: "Substructure technical submission",
      package: "PKG-NB-031",
      submittedBy: "North Structures",
      submittedAt: "2026-08-03T13:30:00Z",
      status: "approved",
      reviewersComplete: 3,
      reviewersTotal: 3,
    },
  ],
  activity: [
    {
      id: "act-1",
      verb: "issued",
      subject: "Coordination federation F32",
      actor: "Project office",
      occurredAt: "2026-08-09T08:12:00Z",
    },
    {
      id: "act-2",
      verb: "updated",
      subject: "External envelope specification",
      actor: "Field & Mason",
      occurredAt: "2026-08-09T08:02:00Z",
    },
    {
      id: "act-3",
      verb: "submitted",
      subject: "Stage 4 coordinated design package",
      actor: "Project office",
      occurredAt: "2026-08-09T06:50:00Z",
    },
  ],
};

export const c0Fixture = deepFreeze(sourceFixture);
